import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import {
  AlertSeverityEnum,
  Plan,
  PlansPagingRes,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchPlans = (params?: GetPlansParams, enabled = true) => {
  return useQuery({
    queryKey: ['plans', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getPlans(params);
    },
    enabled,
  });
};

export const useGetPlan = (id: string) => {
  return useQuery({
    queryKey: ['plans', id],
    queryFn: async () => {
      return gePlan(id);
    },
  });
};

export const useCreatePlan = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Plan de internet creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdatePlan = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updatePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Plan de internet actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemovePlan = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetPlansParams = Partial<Plan> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreatePlanParams = Omit<Plan, 'id_plan'>;
export type UpdatePlanParams = {
  id: string;
  data: CreatePlanParams;
};

export const getPlans = (params?: GetPlansParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<PlansPagingRes>(`${urlBackend}api/v1/plan/?${queryParams}`, true);
};

export const gePlan = async (id: string) => {
  try {
    return await get<Plan>(`${urlBackend}api/v1/plan/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createPlan = async (data: CreatePlanParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Plan>(`${urlBackend}api/v1/plan/`, data, true);
};

export const updatePlan = async ({ id, data }: UpdatePlanParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Plan>(`${urlBackend}api/v1/plan/${id}/`, data, true);
};

export const removePlan = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Plan>(`${urlBackend}api/v1/plan/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
